import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, DollarSign, Calendar, User, Phone, MessageSquare, CheckCircle2, Clock, AlertCircle, Copy, Send, AlertTriangle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { AppLayout } from '../../../components/layout/AppLayout';
import { pickupAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { toast } from 'sonner';
import DisputeFilingModal from '../../../components/pickup/DisputeFilingModal';
import DeliveryTimeline from '../../../components/pickup/DeliveryTimeline';

const DeliveryDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [offers, setOffers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [offerAmount, setOfferAmount] = useState('');
  const [offerMessage, setOfferMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [deliveryCode, setDeliveryCode] = useState('');
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [showDisputeModal, setShowDisputeModal] = useState(false);

  useEffect(() => {
    loadDelivery();
    loadMessages();
    loadOffers();
    
    // Poll for updates every 30 seconds
    const interval = setInterval(() => {
      loadDelivery();
      loadMessages();
      loadOffers();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [id]);

  const loadDelivery = async () => {
    try {
      const { data } = await pickupAPI.getDeliveryDetails(id);
      setDelivery(data.delivery);
    } catch (error) {
      console.error('Load delivery error:', error);
      toast.error('Failed to load delivery');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!delivery || delivery.status === 'posted') return;
    try {
      const { data } = await pickupAPI.getMessages(id);
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Load messages error:', error);
    }
  };

  const loadOffers = async () => {
    if (!isCustomer()) return;
    try {
      const { data } = await pickupAPI.getOffers(id);
      setOffers(data.offers || []);
    } catch (error) {
      console.error('Load offers error:', error);
    }
  };

  const isCustomer = () => delivery && delivery.customer_id === user?.id;
  const isDriver = () => delivery && delivery.driver_id === user?.id;
  const canSeePrivateInfo = () => isCustomer() || isDriver();

  const handleAcceptDelivery = async () => {
    setSubmitting(true);
    try {
      await pickupAPI.acceptDelivery(id);
      toast.success('Job accepted! You can now chat with the customer.');
      loadDelivery();
    } catch (error) {
      console.error('Accept delivery error:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMakeOffer = async () => {
    if (!offerAmount || parseFloat(offerAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      await pickupAPI.makeCounteroffer(id, {
        delivery_id: id,
        offered_amount: parseFloat(offerAmount),
        message: offerMessage
      });
      toast.success('Counteroffer submitted!');
      setShowOfferDialog(false);
      setOfferAmount('');
      setOfferMessage('');
      loadDelivery();
    } catch (error) {
      console.error('Make offer error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit offer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptOffer = async (offerId) => {
    setSubmitting(true);
    try {
      await pickupAPI.acceptOffer(id, offerId);
      toast.success('Offer accepted! Driver has been assigned.');
      loadDelivery();
      loadOffers();
    } catch (error) {
      console.error('Accept offer error:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept offer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectOffer = async (offerId) => {
    try {
      await pickupAPI.rejectOffer(id, offerId);
      toast.success('Offer rejected');
      loadOffers();
    } catch (error) {
      console.error('Reject offer error:', error);
      toast.error('Failed to reject offer');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await pickupAPI.sendMessage(id, newMessage);
      setNewMessage('');
      loadMessages();
    } catch (error) {
      console.error('Send message error:', error);
      toast.error('Failed to send message');
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    setSubmitting(true);
    try {
      await pickupAPI.updateStatus(id, {
        delivery_id: id,
        status: newStatus,
        delivery_code: newStatus === 'delivered' ? deliveryCode : undefined
      });
      toast.success('Status updated');
      setShowStatusDialog(false);
      setDeliveryCode('');
      loadDelivery();
    } catch (error) {
      console.error('Update status error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteDelivery = async () => {
    setSubmitting(true);
    try {
      await pickupAPI.completeDelivery(id);
      toast.success('Delivery completed! Payment released to driver.');
      setShowRatingDialog(true);
      loadDelivery();
    } catch (error) {
      console.error('Complete delivery error:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete delivery');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitRating = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        delivery_id: id,
        rating,
        comment: ratingComment,
        tip_amount: tipAmount ? parseFloat(tipAmount) : 0
      };
      
      await pickupAPI.rateDelivery(id, payload);
      
      if (tipAmount && parseFloat(tipAmount) > 0) {
        toast.success(`Rating and ${delivery.currency} ${tipAmount} tip sent. Thank you!`);
      } else {
        toast.success('Rating submitted. Thank you!');
      }
      
      setShowRatingDialog(false);
      navigate('/pickup/my-jobs');
    } catch (error) {
      console.error('Submit rating error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      posted: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      matched: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      on_the_way: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      ready_for_pickup: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      delivered: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
      completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      disputed: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
      // Legacy
      accepted: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      requested: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      pickup_confirmed: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      in_transit: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      posted: 'Posted',
      matched: 'Matched',
      in_progress: 'In Progress',
      on_the_way: 'On the way',
      ready_for_pickup: 'Ready for pickup',
      delivered: 'Delivered',
      completed: 'Completed',
      cancelled: 'Cancelled',
      disputed: 'Disputed',
      // Legacy
      requested: 'Posted',
      accepted: 'Matched',
      pickup_confirmed: 'In Progress',
      in_transit: 'On the way'
    };
    return labels[status] || status;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
        </div>
      </AppLayout>
    );
  }

  if (!delivery) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Delivery not found</h3>
          <Button onClick={() => navigate('/pickup')}>Go to Pickup Home</Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Delivery Details</h1>
            <p className="text-sm text-muted-foreground">ID: {delivery.id}</p>
          </div>
          <Badge className={getStatusColor(delivery.status)}>
            {getStatusLabel(delivery.status)}
          </Badge>
        </div>

        {/* Route Card */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-950/30 dark:to-green-950/30 p-6 rounded-lg border">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="font-semibold">{delivery.pickup_city}{delivery.pickup_area && `, ${delivery.pickup_area}`}</div>
                  <div className="text-sm text-muted-foreground">{delivery.pickup_country}</div>
                </div>
              </div>
            </div>
            
            <div className="text-3xl text-muted-foreground">→</div>
            
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-green-600" />
                <div>
                  <div className="font-semibold">{delivery.destination_city}{delivery.destination_area && `, ${delivery.destination_area}`}</div>
                  <div className="text-sm text-muted-foreground">{delivery.destination_country}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="details">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">Details</TabsTrigger>
            {canSeePrivateInfo() && <TabsTrigger value="chat">Chat</TabsTrigger>}
            {isCustomer() && delivery.status === 'posted' && <TabsTrigger value="offers">Offers ({offers.length})</TabsTrigger>}
          </TabsList>

          <TabsContent value="details" className="space-y-4">
            {/* Package Info */}
            <div className="bg-card border rounded-lg p-5 space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Package className="h-5 w-5" />
                Package Information
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">Package Type</div>
                  <div className="font-medium">{delivery.package_type.replace('_', ' ')}</div>
                </div>
                {delivery.package_description && (
                  <div>
                    <div className="text-muted-foreground">Description</div>
                    <div className="font-medium">{delivery.package_description}</div>
                  </div>
                )}
                <div>
                  <div className="text-muted-foreground">Pickup Date</div>
                  <div className="font-medium">{new Date(delivery.pickup_date).toLocaleDateString()}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Delivery Fee</div>
                  <div className="font-bold text-green-600">R{delivery.delivery_fee}</div>
                </div>
              </div>
              {delivery.special_instructions && (
                <div>
                  <div className="text-sm text-muted-foreground">Special Instructions</div>
                  <div className="text-sm bg-muted/50 p-3 rounded mt-1">{delivery.special_instructions}</div>
                </div>
              )}
            </div>

            {/* Private Information (after acceptance) */}
            {canSeePrivateInfo() && delivery.status !== 'posted' && (
              <div className="bg-card border rounded-lg p-5 space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  🔒 Private Information
                </h3>
                <div className="space-y-3 text-sm">
                  {delivery.exact_pickup_address && (
                    <div>
                      <div className="text-muted-foreground">Exact Pickup Address</div>
                      <div className="flex items-center gap-2">
                        <div className="font-medium flex-1">{delivery.exact_pickup_address}</div>
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(delivery.exact_pickup_address)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  {delivery.exact_delivery_address && (
                    <div>
                      <div className="text-muted-foreground">Exact Delivery Address</div>
                      <div className="flex items-center gap-2">
                        <div className="font-medium flex-1">{delivery.exact_delivery_address}</div>
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(delivery.exact_delivery_address)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  {delivery.recipient_name && (
                    <div>
                      <div className="text-muted-foreground">Recipient Name</div>
                      <div className="font-medium">{delivery.recipient_name}</div>
                    </div>
                  )}
                  {delivery.recipient_phone && (
                    <div>
                      <div className="text-muted-foreground">Recipient Phone</div>
                      <div className="font-medium">{delivery.recipient_phone}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Customer Info */}
            {isDriver() && (
              <div className="bg-card border rounded-lg p-5 space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Customer
                </h3>
                <div className="text-sm space-y-2">
                  <div>
                    <div className="text-muted-foreground">Name</div>
                    <div className="font-medium">{delivery.customer_name}</div>
                  </div>
                  {delivery.customer_email && (
                    <div>
                      <div className="text-muted-foreground">Email</div>
                      <div className="font-medium">{delivery.customer_email}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Driver Info */}
            {isCustomer() && delivery.driver_id && (
              <div className="bg-card border rounded-lg p-5 space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Driver
                </h3>
                <div className="text-sm space-y-2">
                  <div>
                    <div className="text-muted-foreground">Name</div>
                    <div className="font-medium">{delivery.driver_name}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Delivery Code */}
            {isCustomer() && delivery.status === 'in_transit' && (
              <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-5">
                <h3 className="font-semibold mb-2">📋 Delivery Confirmation Code</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Share this code with the driver when they deliver the package:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white dark:bg-black p-3 rounded font-mono text-2xl font-bold text-center">
                    {delivery.delivery_code}
                  </code>
                  <Button onClick={() => copyToClipboard(delivery.delivery_code)}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </Button>
                </div>
              </div>
            )}

            {/* Status Timeline */}
            <div className="bg-card border rounded-lg p-5 space-y-4">
              <h3 className="font-semibold">Delivery Status</h3>
              <div className="space-y-3">
                {['posted', 'accepted', 'pickup_confirmed', 'in_transit', 'delivered', 'completed'].map((status, index) => {
                  const currentIndex = ['posted', 'accepted', 'pickup_confirmed', 'in_transit', 'delivered', 'completed'].indexOf(delivery.status);
                  const isCompleted = index <= currentIndex;
                  const isCurrent = index === currentIndex;
                  
                  return (
                    <div key={status} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isCompleted ? 'bg-green-600 text-white' : 'bg-muted text-muted-foreground'
                      }`}>
                        {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
                      </div>
                      <div className="flex-1">
                        <div className={`font-medium ${isCurrent ? 'text-primary' : ''}`}>
                          {getStatusLabel(status)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              {/* Driver Actions */}
              {!isCustomer() && delivery.status === 'posted' && (
                <div className="flex gap-3">
                  <Button 
                    className="flex-1 bg-green-600 hover:bg-green-700" 
                    onClick={handleAcceptDelivery}
                    disabled={submitting}
                  >
                    Accept at R{delivery.delivery_fee}
                  </Button>
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowOfferDialog(true)}
                  >
                    Make Counteroffer
                  </Button>
                </div>
              )}

              {isDriver() && delivery.status === 'accepted' && (
                <Button 
                  className="w-full"
                  onClick={() => handleUpdateStatus('pickup_confirmed')}
                  disabled={submitting}
                >
                  Confirm Package Pickup
                </Button>
              )}

              {isDriver() && delivery.status === 'pickup_confirmed' && (
                <Button 
                  className="w-full"
                  onClick={() => handleUpdateStatus('in_transit')}
                  disabled={submitting}
                >
                  Mark as In Transit
                </Button>
              )}

              {isDriver() && delivery.status === 'in_transit' && (
                <Button 
                  className="w-full"
                  onClick={() => setShowStatusDialog(true)}
                >
                  Confirm Delivery
                </Button>
              )}

              {isCustomer() && delivery.status === 'delivered' && (
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={handleCompleteDelivery}
                  disabled={submitting}
                >
                  Confirm Received & Complete
                </Button>
              )}

              {/* Dispute Button */}
              {(isCustomer() || isDriver()) && 
               ['delivered', 'completed', 'in_transit', 'pickup_confirmed'].includes(delivery.status) && 
               !delivery.disputed && (
                <Button 
                  variant="destructive"
                  className="w-full"
                  onClick={() => setShowDisputeModal(true)}
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  File a Dispute
                </Button>
              )}

              {delivery.disputed && (
                <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                  <p className="text-sm font-medium text-orange-800 dark:text-orange-400">
                    ⚠️ This delivery is under dispute
                  </p>
                  <p className="text-xs text-orange-700 dark:text-orange-500 mt-1">
                    Our team is reviewing the issue. You'll be notified of the resolution.
                  </p>
                </div>
              )}
            </div>

            {/* Delivery Timeline */}
            <DeliveryTimeline deliveryId={delivery.id} />
          </TabsContent>

          {/* Chat Tab */}
          {canSeePrivateInfo() && (
            <TabsContent value="chat" className="space-y-4">
              <div className="bg-card border rounded-lg p-5 space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Private Chat
                </h3>
                
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {messages.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No messages yet. Start the conversation!
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div 
                        key={msg.id} 
                        className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] rounded-lg p-3 ${
                          msg.sender_id === user?.id 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-muted'
                        }`}>
                          <div className="text-xs opacity-70 mb-1">{msg.sender_name}</div>
                          <div className="text-sm">{msg.message}</div>
                          <div className="text-xs opacity-70 mt-1">
                            {new Date(msg.created_at).toLocaleTimeString()}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="flex gap-2">
                  <Input
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  />
                  <Button onClick={handleSendMessage}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </TabsContent>
          )}

          {/* Offers Tab */}
          {isCustomer() && delivery.status === 'posted' && (
            <TabsContent value="offers" className="space-y-4">
              {offers.length === 0 ? (
                <div className="text-center py-12 bg-muted/50 rounded-lg">
                  <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No offers yet</h3>
                  <p className="text-muted-foreground">Drivers will see your delivery and can make offers</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {offers.map((offer) => (
                    <div key={offer.id} className="bg-card border rounded-lg p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="font-semibold">{offer.driver_name}</div>
                          <div className="text-sm text-muted-foreground">
                            {new Date(offer.created_at).toLocaleString()}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-600">R{offer.offered_amount}</div>
                          <Badge variant={offer.status === 'pending' ? 'secondary' : offer.status === 'accepted' ? 'default' : 'outline'}>
                            {offer.status}
                          </Badge>
                        </div>
                      </div>
                      
                      {offer.message && (
                        <div className="bg-muted/50 p-3 rounded text-sm mb-3">
                          {offer.message}
                        </div>
                      )}

                      {offer.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            onClick={() => handleAcceptOffer(offer.id)}
                            disabled={submitting}
                          >
                            Accept Offer
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => handleRejectOffer(offer.id)}
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* Counteroffer Dialog */}
        <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Make Counteroffer</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Your Offer (ZAR)</Label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={offerAmount}
                  onChange={(e) => setOfferAmount(e.target.value)}
                  min="1"
                  step="0.01"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Customer's offer: R{delivery.delivery_fee}
                </p>
              </div>
              <div>
                <Label>Message (Optional)</Label>
                <Textarea
                  placeholder="Explain your offer..."
                  value={offerMessage}
                  onChange={(e) => setOfferMessage(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowOfferDialog(false)}>Cancel</Button>
              <Button onClick={handleMakeOffer} disabled={submitting}>Submit Offer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delivery Code Dialog */}
        <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Delivery</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enter the 6-digit delivery code provided by the recipient to confirm delivery:
              </p>
              <div>
                <Label>Delivery Code</Label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={deliveryCode}
                  onChange={(e) => setDeliveryCode(e.target.value)}
                  maxLength={6}
                  className="text-2xl text-center font-mono"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowStatusDialog(false)}>Cancel</Button>
              <Button onClick={() => handleUpdateStatus('delivered')} disabled={submitting || deliveryCode.length !== 6}>
                Confirm Delivery
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Rating Dialog */}
        <Dialog open={showRatingDialog} onOpenChange={setShowRatingDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rate this Delivery</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>How was your experience?</Label>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      className={`text-3xl ${rating >= star ? 'text-yellow-400' : 'text-gray-300'}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Comment (Optional)</Label>
                <Textarea
                  placeholder="Share your feedback..."
                  value={ratingComment}
                  onChange={(e) => setRatingComment(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRatingDialog(false)}>Skip</Button>
              <Button onClick={handleSubmitRating} disabled={submitting}>Submit Rating</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dispute Filing Modal */}
        {showDisputeModal && (
          <DisputeFilingModal
            delivery={delivery}
            onClose={() => setShowDisputeModal(false)}
            onSuccess={() => {
              loadDelivery();
              toast.success('Dispute filed successfully');
            }}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default DeliveryDetailsPage;
